import { Building2, CalendarCheck2, CalendarRange, Gavel, Globe, Mail, MapPin } from "lucide-react";

import { HorseIcon } from "@/components/brand/HorseIcon";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Countdown } from "@/components/Countdown";
import { ResilientImage } from "@/components/media/ResilientImage";
import { PedigreeTree } from "@/components/PedigreeTree";
import { TalkToUs } from "@/components/TalkToUs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useServerCountdown } from "@/hooks/useSockets";
import { mediaUrlFromMap } from "@/lib/api";
import { formatCurrency, formatDateTime, localized } from "@/lib/intl";
import { eventBase } from "@/lib/microsite";
import { termKey } from "@/lib/terminology";

/*
 * Block-based landing renderer. `BLOCK_REGISTRY` maps block type -> component.
 * Adding a new block type later = add one component here, zero refactor.
 * Every block receives: { data: {event, organizer, auctions, lots, media}, props }
 */

const Section = ({ children, testid, tight = false }) => (
  <section data-testid={testid} className={`max-w-6xl mx-auto px-6 lg:px-12 ${tight ? "py-12" : "py-16 lg:py-24"}`}>
    {children}
  </section>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-2xl sm:text-3xl font-medium tracking-tight text-[#0A0A0A] mb-10">{children}</h2>
);

/* ---------------- hero ---------------- */

const HeroBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { event, organizer, auctions, lots, media } = data;
  const single = lots.length === 1;
  const heroUrl = mediaUrlFromMap(media, props?.media_id || event.hero_media_id);
  const firstAuction = auctions[0];
  const started = firstAuction && new Date(firstAuction.starts_at) < new Date();
  // live/hybrid sessions have no meaningful "ends in" — the hammer is the clock
  const isLiveType = Boolean(firstAuction && ["live", "hybrid"].includes(firstAuction.type));
  const target = firstAuction && (started ? (isLiveType ? null : firstAuction.ends_at) : firstAuction.starts_at);
  // builder overrides win; event data is the default
  const headline = localized(props?.headline, locale) || localized(event.title, locale);
  const subheadline = localized(props?.subheadline, locale) || localized(event.description, locale);
  const ctaLabel = localized(props?.cta_label, locale) || t(termKey(single, "cta"));
  const variant = props?.variant || data.theme?.hero_style || "image_full";
  const dark = variant === "image_full";

  const meta = (
    <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
      {target && (
        <div className="flex flex-col gap-2">
          <span className={`text-[10px] font-semibold uppercase tracking-[0.25em] ${dark ? "text-white/50" : "text-[#0A0A0A]/40"}`}>
            {started ? t("pub.endsIn") : t("pub.startsIn")}
          </span>
          <div className={dark ? "[&_*]:!text-white [&_.text-\\[\\#0A0A0A\\]\\/20]:!text-white/30" : ""}>
            <Countdown target={target} />
          </div>
        </div>
      )}
      {isLiveType && started && (
        <span
          data-testid="hero-live-session-chip"
          className="inline-flex items-center gap-2 rounded-full bg-[#B42318] text-white px-4 h-8 text-[11px] font-bold uppercase tracking-[0.14em]"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" aria-hidden="true" />
          {t("live.liveNow")}
        </span>
      )}
      <Badge
        className={
          dark
            ? "bg-white/10 text-white hover:bg-white/10 border border-white/20 h-8 px-4 backdrop-blur-sm"
            : "bg-black/5 text-[#0A0A0A] hover:bg-black/5 border border-black/10 h-8 px-4"
        }
      >
        {t(termKey(single, "count"), { count: lots.length })}
      </Badge>
      {event.venue?.city && (
        <span className={`inline-flex items-center gap-2 text-sm ${dark ? "text-white/70" : "text-[#0A0A0A]/60"}`}>
          <MapPin className="w-4 h-4" aria-hidden="true" />
          {[event.venue.name, event.venue.city, event.venue.country].filter(Boolean).join(", ")}
        </span>
      )}
    </div>
  );

  /* Fase 12B: formato + fechas integrados EN el hero (antes vivían en un
     módulo lateral aislado que rompía la composición y dejaba hueco vacío). */
  const infoCols = firstAuction
    ? [
        {
          key: "format",
          icon: Gavel,
          label: t("pub.auctionFormat"),
          value: t(firstAuction.type === "live" ? "pub.typeLive" : firstAuction.type === "hybrid" ? "pub.typeHybrid" : "pub.typeTimed"),
        },
        {
          key: "starts",
          icon: CalendarRange,
          label: t("pub.startsAt"),
          value: formatDateTime(firstAuction.starts_at, locale),
        },
        ...(firstAuction.ends_at && !isLiveType
          ? [{
              key: "ends",
              icon: CalendarCheck2,
              label: t("pub.endsAt"),
              value: formatDateTime(firstAuction.ends_at, locale),
            }]
          : []),
      ]
    : [];

  const infoStrip = infoCols.length > 0 && (
    <div
      data-testid="hero-info-strip"
      className={`flex flex-wrap gap-x-10 gap-y-4 pt-6 border-t ${dark ? "border-white/15" : "border-black/8"}`}
    >
      {infoCols.map(({ key, icon: Icon, label, value }) => (
        <div key={key} className="flex items-start gap-3" data-testid={`hero-info-${key}`}>
          <span
            className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${dark ? "bg-white/10" : "bg-black/5"}`}
            aria-hidden="true"
          >
            <Icon className={`w-3.5 h-3.5 ${dark ? "text-white/80" : "text-[#0A0A0A]/60"}`} />
          </span>
          <span className="flex flex-col gap-0.5">
            <span className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${dark ? "text-white/50" : "text-[#0A0A0A]/40"}`}>
              {label}
            </span>
            <span className={`text-sm font-medium tabular-nums ${dark ? "text-white" : "text-[#0A0A0A]"}`}>{value}</span>
          </span>
        </div>
      ))}
    </div>
  );

  const cta = (
    <Button
      asChild
      className={`w-fit rounded-full h-12 px-8 transition-colors duration-300 ${
        dark ? "bg-white text-[#0A0A0A] hover:bg-white/90" : "bg-[#0A0A0A] text-white hover:bg-black/80"
      }`}
    >
      <a href="#lots" data-testid="landing-hero-cta">{ctaLabel}</a>
    </Button>
  );

  if (variant === "minimal") {
    return (
      <section className="relative bg-white" data-testid="block-hero">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-16 lg:pb-24 flex flex-col gap-8 border-t-2 border-[#0A0A0A]">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0A0A0A]/40">
            {t("pub.organizedBy")} {organizer.name}
          </span>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[#0A0A0A] max-w-4xl leading-[1.02] brand-heading" data-testid="landing-title">
            {headline}
          </h1>
          <p className="text-base lg:text-lg text-[#0A0A0A]/60 leading-relaxed max-w-2xl">{subheadline}</p>
          {meta}
          {cta}
          {infoStrip}
        </div>
      </section>
    );
  }

  if (variant === "split") {
    return (
      <section className="relative bg-white" data-testid="block-hero">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="flex flex-col gap-7">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#0A0A0A]/40">
              {t("pub.organizedBy")} {organizer.name}
            </span>
            <h1 className="text-4xl sm:text-5xl font-light tracking-tighter text-[#0A0A0A] leading-[1.05] brand-heading" data-testid="landing-title">
              {headline}
            </h1>
            <p className="text-base text-[#0A0A0A]/60 leading-relaxed">{subheadline}</p>
            {meta}
            {cta}
          </div>
          {heroUrl && (
            <div className="relative">
              <ResilientImage src={heroUrl} alt="" className="w-full h-80 lg:h-[30rem] rounded-3xl border border-black/5" testId="hero-image" />
            </div>
          )}
        </div>
      </section>
    );
  }

  return (
    <section className="relative bg-[#0A0A0A] overflow-hidden eq-grain" data-testid="block-hero">
      {heroUrl && (
        <img src={heroUrl} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="eager" />
      )}
      <div className="relative max-w-6xl mx-auto px-6 lg:px-12 py-24 lg:py-36 flex flex-col gap-8">
        <span className="text-xs font-semibold uppercase tracking-[0.25em] text-white/60">
          {t("pub.organizedBy")} {organizer.name}
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-white max-w-3xl leading-[1.05] brand-heading" data-testid="landing-title">
          {headline}
        </h1>
        <p className="text-base lg:text-lg text-white/70 leading-relaxed max-w-2xl">{subheadline}</p>
        {meta}
        {cta}
        {infoStrip}
      </div>
    </section>
  );
};

/* ---------------- event info ---------------- */

const EventInfoBlock = ({ data }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { event, auctions } = data;
  const description = localized(event.description, locale);
  // Fase 12B: formato y fechas viven ahora DENTRO del hero — el módulo lateral
  // aislado rompía la composición. Solo se conserva una lista compacta de
  // sesiones cuando el evento tiene varias subastas (multi-sesión).
  const multiSession = auctions.length > 1;
  if (!description && !multiSession) return null;
  return (
    <Section testid="block-event-info">
      <div className={`grid grid-cols-1 ${multiSession ? "lg:grid-cols-3 gap-12" : "gap-8"}`}>
        {description && (
          <div className={`${multiSession ? "lg:col-span-2" : "max-w-3xl"} flex flex-col gap-4`}>
            {/* Phase 6: no artificial "About this event" header — the organizer's
                description reads naturally as an editorial lede. */}
            <p className="text-xl lg:text-2xl font-light text-[#0A0A0A]/80 leading-relaxed tracking-tight">
              {description}
            </p>
          </div>
        )}
        {multiSession && (
          <aside className="flex flex-col gap-5 bg-[#F9F9F9] rounded-2xl border border-black/5 p-6 h-fit">
            {auctions.map((a) => (
              <div key={a.id} className="flex flex-col gap-1">
                <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/40">
                  {t("pub.auctionFormat")}
                </span>
                <span className="text-sm font-medium text-[#0A0A0A]">
                  {t(a.type === "live" ? "pub.typeLive" : a.type === "hybrid" ? "pub.typeHybrid" : "pub.typeTimed")}
                  {a.title?.en ? ` — ${localized(a.title, locale)}` : ""}
                </span>
                <div className="flex flex-col gap-1 pt-1">
                  <span className="inline-flex items-center gap-2 text-xs text-[#737373]">
                    <CalendarRange className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                    <span className="font-medium text-[#0A0A0A]/60">{t("pub.startsAt")}</span>
                    <span className="tabular-nums">{formatDateTime(a.starts_at, locale)}</span>
                  </span>
                  <span className="inline-flex items-center gap-2 text-xs text-[#737373]">
                    <span className="w-3.5 shrink-0" aria-hidden="true" />
                    <span className="font-medium text-[#0A0A0A]/60">{t("pub.endsAt")}</span>
                    <span className="tabular-nums">{formatDateTime(a.ends_at, locale)}</span>
                  </span>
                </div>
              </div>
            ))}
          </aside>
        )}
      </div>
    </Section>
  );
};

/* ---------------- schedule ---------------- */

const ScheduleBlock = ({ data }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { event } = data;
  if (!event.schedule?.length) return null;
  return (
    <Section testid="block-schedule">
      <SectionTitle>{t("pub.schedule")}</SectionTitle>
      <ol className="flex flex-col gap-0">
        {event.schedule.map((item, i) => (
          <li key={i} className="relative flex gap-6 pb-10 last:pb-0">
            <div className="flex flex-col items-center">
              <span className="w-3 h-3 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: "var(--brand-accent, #1D4D3B)" }} aria-hidden="true" />
              {i < event.schedule.length - 1 && <span className="w-px flex-1 bg-black/10" aria-hidden="true" />}
            </div>
            <div className="flex flex-col gap-1 -mt-0.5">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/40">
                {formatDateTime(item.starts_at, locale)}
              </span>
              <h3 className="text-lg font-medium text-[#0A0A0A]">{localized(item.title, locale)}</h3>
              <p className="text-sm text-[#737373] leading-relaxed max-w-xl">{localized(item.description, locale)}</p>
            </div>
          </li>
        ))}
      </ol>
    </Section>
  );
};

/* ---------------- lots grid ---------------- */

const GENETIC_TYPES = ["embryo", "semen_dose", "breeding_right"];

/* Countdown en vivo de la tarjeta — reutiliza useServerCountdown con el offset
   de hora del servidor del socket del evento (mismo patrón que lote/dock). */
const ZERO_REF = { current: 0 };
const CardCountdown = ({ target, offsetRef, testid }) => {
  const p = useServerCountdown(target, offsetRef || ZERO_REF);
  if (p.total <= 0) return null;
  const seg = (v) => String(v).padStart(2, "0");
  return (
    <span
      data-testid={testid}
      role="timer"
      className={`text-[11px] tabular-nums ${p.urgent ? "text-[#B42318] font-semibold" : "text-[#737373]"}`}
    >
      {p.days > 0 ? `${p.days}d ` : ""}
      {seg(p.hours)}:{seg(p.minutes)}:{seg(p.seconds)}
    </span>
  );
};

const LotCard = ({ lot, horse, media, locale, t, single, base, personal, offsetRef }) => {
  const cover = mediaUrlFromMap(media, horse?.cover_media_id);
  const genetic = GENETIC_TYPES.includes(horse?.asset_type);
  const hasBids = lot.bid_count > 0;
  const closed = ["sold", "passed"].includes(lot.status);
  // per-lot opening: scheduled lot shows its own future start on the card
  const opensLater = !closed && lot.status === "upcoming" && lot.starts_at && new Date(lot.starts_at) > new Date();
  const subtitle = genetic
    ? [
        horse?.sire_info?.name && horse?.dam_info?.name && `${horse.sire_info.name} × ${horse.dam_info.name}`,
        horse?.expected_birth_date && `${t("pub.expectedBirth")} ${new Date(horse.expected_birth_date).getFullYear()}`,
      ]
        .filter(Boolean)
        .join(" · ")
    : [horse?.year_of_birth, horse?.sex && t(`horse.${horse.sex}`), horse?.studbook, horse?.disciplines?.[0] && t(`disc.${horse.disciplines[0]}`, { defaultValue: horse.disciplines[0] })]
        .filter(Boolean)
        .join(" · ");
  return (
    <Link
      to={base ? `${base}/lots/${lot.id}` : `/horses/${horse?.id}`}
      data-testid={`lot-card-${lot.lot_number}`}
      className="group bg-white rounded-2xl border border-black/5 overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-[0_16px_40px_rgb(0,0,0,0.08)] transition-[transform,box-shadow] duration-300"
    >
      <div className="relative h-64 bg-[#F9F9F9] overflow-hidden">
        {cover ? (
          <ResilientImage
            src={cover}
            alt={horse?.name}
            className="w-full h-full"
            imgClassName="group-hover:scale-[1.03] transition-transform duration-500 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <HorseIcon className="w-10 h-10 text-black/10" aria-hidden="true" />
          </div>
        )}
        <span className="absolute top-3 left-3 backdrop-blur-xl bg-white/80 rounded-full px-3 py-1 text-xs font-semibold text-[#0A0A0A]">
          {t(termKey(single, "lotN"), { number: lot.lot_number })}
        </span>
        {lot.no_reserve && (
          <span
            className="absolute top-3 right-3 text-white rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider"
            style={{ backgroundColor: "var(--brand-accent, #1D4D3B)" }}
          >
            {t("pub.noReserve")}
          </span>
        )}
        {horse?.asset_type && horse.asset_type !== "sport_horse" && (
          <span
            className="absolute bottom-3 left-3 backdrop-blur-xl bg-[#0A0A0A]/70 text-white rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider"
            data-testid={`lot-asset-type-${lot.lot_number}`}
          >
            {/* C4: los embriones muestran congelado/implantado en el catálogo */}
            {horse.asset_type === "embryo" && horse.embryo_state
              ? `${t("asset.embryo")} • ${t(`horse.${horse.embryo_state}Short`)}`
              : t(`asset.${horse.asset_type}`)}
          </span>
        )}
        {/* Estado personal del comprador logueado — un solo badge discreto */}
        {!closed && personal && (
          <span
            className={`absolute bottom-3 right-3 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${
              personal === "leading"
                ? "bg-[#1D4D3B] text-white"
                : personal === "outbid"
                  ? "backdrop-blur-xl bg-white/85 text-[#B42318]"
                  : "backdrop-blur-xl bg-white/85 text-[#737373]"
            }`}
            data-testid={`lot-personal-${lot.lot_number}`}
          >
            {t(personal === "leading" ? "bid.dockLeading" : personal === "outbid" ? "bid.dockOutbid" : "bid.watching")}
          </span>
        )}
      </div>
      <div className="p-5 flex flex-col gap-2 flex-1">
        <h3 className="text-lg font-medium text-[#0A0A0A]">{horse?.name}</h3>
        <p className="text-sm text-[#737373]">{subtitle}</p>
        <div className="mt-auto pt-3 flex items-center justify-between border-t border-black/5">
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#0A0A0A]/40 inline-flex items-center gap-1.5">
            {closed
              ? lot.status === "sold" ? t("bid.sold") : t("bid.notSold")
              : opensLater ? (
                <span data-testid={`lot-opens-later-${lot.lot_number}`}>{t("pub.opensOn", { date: formatDateTime(lot.starts_at, locale) })}</span>
              ) : hasBids ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: "var(--brand-accent, #1D4D3B)" }} aria-hidden="true" />
                  {t("bid.currentBid")}
                </>
              ) : t("pub.startingPrice")}
          </span>
          <span className="flex flex-col items-end">
            <span className="text-base font-medium text-[#0A0A0A] tabular-nums" data-testid={`lot-price-${lot.lot_number}`}>
              {closed
                ? lot.final_price_cents ? formatCurrency(lot.final_price_cents, lot.currency, locale) : "—"
                : formatCurrency(hasBids ? lot.current_price_cents : lot.starting_price_cents, lot.currency, locale)}
            </span>
            {!closed && !opensLater && lot.ends_at && (
              <CardCountdown target={lot.ends_at} offsetRef={offsetRef} testid={`lot-countdown-${lot.lot_number}`} />
            )}
          </span>
        </div>
      </div>
    </Link>
  );
};

const LotsGridBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { lots, media } = data;
  const single = lots.length === 1;
  return (
    <section id="lots" className="bg-[#F9F9F9] border-y border-black/5" data-testid="block-lots-grid">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <SectionTitle>{localized(props?.title, locale) || t(termKey(single, "heading"))}</SectionTitle>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {lots.map(({ lot, horse }) => (
            <LotCard key={lot.id} lot={lot} horse={horse} media={media} locale={locale} t={t} single={single} base={data.event?.slug ? eventBase(data.organizer?.slug, data.event.slug) : null} personal={data.personalLots?.[lot.id]} offsetRef={data.offsetRef} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------------- horse showcase (single-horse feature) ---------------- */

const HorseShowcaseBlock = ({ data }) => {
  const { i18n } = useTranslation();
  const first = data.lots[0];
  if (!first?.horse?.pedigree) return null;
  return (
    <Section testid="block-horse-showcase">
      <SectionTitle>{first.horse.name}</SectionTitle>
      <p className="text-base text-[#0A0A0A]/70 leading-relaxed max-w-2xl -mt-6 mb-10">
        {localized(first.horse.description, i18n.resolvedLanguage)}
      </p>
      <PedigreeTree pedigree={first.horse.pedigree} />
    </Section>
  );
};

/* ---------------- gallery ---------------- */

const GalleryBlock = ({ data, props }) => {
  const { t } = useTranslation();
  const { lots, media } = data;
  // builder-curated selection wins; falls back to the horses' photo pool.
  // Unlimited images — every configured media id renders.
  const ids = props?.media_ids?.length
    ? props.media_ids
    : [...new Set(lots.flatMap(({ horse }) => horse?.media_ids || []))];
  const urls = ids.map((id) => mediaUrlFromMap(media, id)).filter(Boolean);
  if (urls.length === 0) return null;
  return (
    <Section testid="block-gallery">
      <SectionTitle>{t("pub.gallery")}</SectionTitle>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {urls.map((url, i) => (
          <img
            key={i}
            src={url}
            alt=""
            className={`w-full object-cover rounded-2xl border border-black/5 ${i % 3 === 0 ? "h-64" : "h-48"}`}
            loading="lazy"
          />
        ))}
      </div>
    </Section>
  );
};

/* ---------------- organizer profile ---------------- */

const OrganizerProfileBlock = ({ data }) => {
  const { t, i18n } = useTranslation();
  const { organizer } = data;
  return (
    <Section testid="block-organizer-profile">
      <div className="bg-white rounded-3xl border border-black/5 p-8 lg:p-12 flex flex-col sm:flex-row gap-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
        <div className="w-16 h-16 rounded-2xl bg-[#0A0A0A] flex items-center justify-center shrink-0">
          <Building2 className="w-7 h-7 text-white" aria-hidden="true" />
        </div>
        <div className="flex flex-col gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.25em]" style={{ color: "var(--brand-accent, #1D4D3B)" }}>
            {t("pub.organizedBy")}
          </span>
          <h2 className="text-2xl font-medium tracking-tight text-[#0A0A0A]">{organizer.name}</h2>
          <p className="text-sm text-[#737373] leading-relaxed max-w-xl">
            {localized(organizer.description, i18n.resolvedLanguage)}
          </p>
          <div className="flex flex-wrap items-center gap-5 pt-1">
            {organizer.location && (
              <span className="inline-flex items-center gap-2 text-xs text-[#737373]">
                <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                {organizer.location}
              </span>
            )}
            {organizer.website && (
              <a
                href={organizer.website}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xs font-medium text-[#0A0A0A] hover:text-[#1D4D3B] transition-colors duration-200"
              >
                <Globe className="w-3.5 h-3.5" aria-hidden="true" />
                {t("pub.visitWebsite")}
              </a>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

/* ---------------- sponsors ---------------- */

const SPONSOR_TIERS = ["principal", "gold", "collaborator"];
const SPONSOR_SIZE = { principal: "lg", gold: "md", collaborator: "sm" };

const SponsorMark = ({ sponsor, media, size }) => {
  const url = mediaUrlFromMap(media, sponsor.logo_media_id);
  const imgCls = {
    lg: "h-16 sm:h-20 max-w-[260px]",
    md: "h-10 sm:h-12 max-w-[180px]",
    sm: "h-8 max-w-[140px]",
  }[size];
  const txtCls = {
    lg: "text-2xl sm:text-3xl",
    md: "text-xl",
    sm: "text-base",
  }[size];
  const inner = url ? (
    <img
      src={url}
      alt={sponsor.name}
      loading="lazy"
      className={`${imgCls} w-auto object-contain grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-[filter,opacity] duration-300`}
    />
  ) : (
    <span className={`${txtCls} font-medium tracking-tight text-[#0A0A0A]/35 hover:text-[#0A0A0A]/70 transition-colors duration-300`}>
      {sponsor.name}
    </span>
  );
  if (!sponsor.url) return inner;
  return (
    <a
      href={sponsor.url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      aria-label={sponsor.name}
      data-testid={`sponsor-link-${sponsor.id || sponsor.name}`}
      className="inline-flex items-center"
    >
      {inner}
    </a>
  );
};

const SponsorsBlock = ({ data }) => {
  const { t } = useTranslation();
  const sponsors = data.sponsors || [];
  if (!sponsors.length) return null;
  const groups = SPONSOR_TIERS.map((tier) => [tier, sponsors.filter((s) => s.tier === tier)]).filter(([, g]) => g.length);
  return (
    <Section testid="block-sponsors" tight>
      <span className="block text-center text-[10px] font-semibold uppercase tracking-[0.3em] text-[#0A0A0A]/40 mb-10">
        {t("pub.sponsors")}
      </span>
      <div className="flex flex-col gap-10">
        {groups.map(([tier, list]) => (
          <div key={tier} className="flex flex-col items-center gap-5" data-testid={`sponsor-tier-${tier}`}>
            {groups.length > 1 && (
              <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[#0A0A0A]/25">
                {t(`pub.tier_${tier}`)}
              </span>
            )}
            <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6">
              {list.map((s) => (
                <SponsorMark key={s.id || s.name} sponsor={s} media={data.media} size={SPONSOR_SIZE[tier]} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

/* ---------------- faq ---------------- */

const FaqBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const items = props?.items || [];
  if (items.length === 0) return null;
  return (
    <Section testid="block-faq">
      <SectionTitle>{t("pub.faq")}</SectionTitle>
      <Accordion type="single" collapsible className="max-w-3xl">
        {items.map((item, i) => (
          <AccordionItem key={i} value={`faq-${i}`} className="border-black/5">
            <AccordionTrigger className="text-left text-base font-medium text-[#0A0A0A] hover:no-underline">
              {localized(item.q, locale)}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-[#737373] leading-relaxed">
              {localized(item.a, locale)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      <div className="max-w-3xl">
        <TalkToUs email={data.event.contact_email || data.organizer.contact_email || undefined} testid="block-faq-talk-to-us" />
      </div>
    </Section>
  );
};

/* ---------------- contact ---------------- */

const ContactBlock = ({ data, props }) => {
  const { t, i18n } = useTranslation();
  const email = data.event.contact_email || data.organizer.contact_email;
  if (!email) return null;
  // Editable section title (client review): organizer copy wins, "Contact Us" default.
  const title = localized(props?.title, i18n.resolvedLanguage) || t("pub.contact");
  return (
    <Section testid="block-contact" tight>
      <div className="bg-[#0A0A0A] rounded-3xl px-6 sm:px-8 lg:px-12 py-10 sm:py-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-white" data-testid="block-contact-title">{title}</h2>
        <Button asChild className="rounded-full h-12 px-5 sm:px-8 bg-white text-[#0A0A0A] hover:bg-white/90 transition-colors duration-300 max-w-full w-full sm:w-auto justify-center min-w-0">
          <a href={`mailto:${email}`} data-testid="landing-contact-cta">
            <Mail className="w-4 h-4 mr-2 shrink-0" aria-hidden="true" />
            <span className="truncate">{email}</span>
          </a>
        </Button>
      </div>
    </Section>
  );
};

/* ---------------- conditions (organizer legal terms) ---------------- */

const ConditionsBlock = ({ data }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const sections = (data.terms?.sections || []).filter((s) => localized(s.content, locale));
  // P0 única fuente de verdad: el backend expone SIEMPRE los bps efectivos
  // (None -> defaults de plataforma) y genera la sección "commission" del
  // acordeón en tiempo real — aquí solo se renderiza (?? = misma red que backend).
  const sellerBps = data.event?.seller_commission_bps ?? 1000;
  const buyerBps = data.event?.buyer_premium_bps ?? 0;
  const pct = (bps) => (bps / 100).toLocaleString(locale);
  return (
    <Section testid="block-conditions">
      <SectionTitle>{t("terms.title")}</SectionTitle>
      <div className="flex flex-wrap gap-2.5 mb-8 max-w-3xl" data-testid="conditions-commissions">
        <span
          className="inline-flex items-center rounded-full border border-black/10 bg-black/[0.03] px-4 py-2 text-sm font-medium text-[#0A0A0A]"
          data-testid="conditions-seller-commission"
        >
          {t("terms.sellerCommissionLine", { pct: pct(sellerBps) })}
        </span>
        <span
          className="inline-flex items-center rounded-full border border-black/10 bg-black/[0.03] px-4 py-2 text-sm font-medium text-[#0A0A0A]"
          data-testid="conditions-buyer-premium"
        >
          {t("terms.buyerPremiumLine", { pct: pct(buyerBps) })}
        </span>
      </div>
      {sections.length > 0 && (
      <Accordion type="single" collapsible className="max-w-3xl">
        {sections.map((s) => (
          <AccordionItem key={s.key} value={s.key} className="border-black/5">
            <AccordionTrigger className="text-left text-base font-medium text-[#0A0A0A] hover:no-underline">
              {t(`terms.${s.key}`)}
            </AccordionTrigger>
            <AccordionContent className="text-sm text-[#737373] leading-relaxed whitespace-pre-line">
              {localized(s.content, locale)}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
      )}
    </Section>
  );
};

/* ---------------- registry ---------------- */

import { EXTENDED_BLOCKS } from "@/components/blocks/extended";

export const BLOCK_REGISTRY = {
  hero: HeroBlock,
  event_info: EventInfoBlock,
  schedule: ScheduleBlock,
  lots_grid: LotsGridBlock,
  horse_showcase: HorseShowcaseBlock,
  gallery: GalleryBlock,
  organizer_profile: OrganizerProfileBlock,
  sponsors: SponsorsBlock,
  faq: FaqBlock,
  conditions: ConditionsBlock,
  contact: ContactBlock,
  ...EXTENDED_BLOCKS,
};
